import React, { useRef } from 'react';
import { Github, GraduationCap, Users, TrendingUp, Linkedin, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

// --- Card Component ---
const Card = ({ children, className }) => (
  <div className={`modern-card ${className}`}>
    {children}
  </div>
);

// --- Button Component ---
const Button = ({ variant, size, children, className, ...props }) => (
  <button
    className={`modern-btn ${variant} ${size} ${className}`}
    {...props}
  >
    {children}
  </button>
);

// --- StudentCard Component ---
const StudentCard = ({ student }) => {
  const avatarSrc = student.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(student.name)}&background=random&color=fff&size=128`;

  return (
    <Card className="student-card-wrapper">
      <div className="card-header-bg"></div>
      <div className="card-body">
        <div className="avatar-wrapper">
          <div className="avatar-glow"></div>
          <img 
            src={avatarSrc} 
            alt={student.name} 
            className="avatar-img"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(student.name)}&background=random&color=fff&size=128`;
            }}
          />
          <div className="status-indicator"></div>
        </div>
        
        <div className="student-identity">
          <h3 className="student-name">{student.name}</h3>
          <p className="student-username">@{student.username}</p>
        </div>
        
        <p className="student-blurb">{student.blurb}</p>

        <div className="action-stack">
          {student.githubUrl && (
            <a href={student.githubUrl} target="_blank" rel="noopener noreferrer" className="btn-link">
              <Button variant="github" size="lg">
                <Github size={18} />
                <span>Project on GitHub</span>
              </Button>
            </a>
          )}

          {student.linkedinUrl && (
            <a href={student.linkedinUrl} target="_blank" rel="noopener noreferrer" className="btn-link">
              <Button variant="linkedin" size="lg">
                <Linkedin size={18} />
                <span>Profile on LinkedIn</span>
              </Button>
            </a>
          )}
        </div>
      </div>
    </Card>
  );
};

// --- Manual Scroll Row Component ---
const ScrollRow = ({ students, id }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340; // Card width (320px) + gap (20px)
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="scroll-row-container">
      {/* Left Button */}
      <button 
        className="nav-btn left" 
        onClick={() => scroll('left')}
        aria-label="Scroll left"
      >
        <ChevronLeft size={24} />
      </button>

      {/* Scrollable Area */}
      <div className="scroll-content" ref={scrollRef}>
        {students.map((student, i) => (
          <StudentCard key={`${id}-${i}-${student.username}`} student={student} />
        ))}
      </div>

      {/* Right Button */}
      <button 
        className="nav-btn right" 
        onClick={() => scroll('right')}
        aria-label="Scroll right"
      >
        <ChevronRight size={24} />
      </button>
      
      {/* Edge Gradients for visual cue */}
      <div className="edge-gradient left-gradient" />
      <div className="edge-gradient right-gradient" />
    </div>
  );
};

// --- Main Component ---
const CommunitySection = () => {
  
  const getBlurb = (index) => {
    const blurbs = [
      "AI & Machine Learning Practitioner exploring deep learning models.",
      "Data Scientist focused on predictive analytics and business intelligence.",
      "Building end-to-end ML pipelines and deployment strategies.",
      "Python developer passionate about solving real-world data problems.",
      "Exploring the frontiers of Generative AI and NLP.",
      "Data Analyst transforming raw data into actionable insights.",
      "Machine Learning Engineer building scalable AI solutions."
    ];
    return blurbs[index % blurbs.length];
  };

  const students = [
    // --- ORIGINAL LIST ---
    {
      name: "Monesh Venkul Vommi",
      username: "moneshvenkul",
      avatar: "assets/images/mentor/monesh.jpg",
      blurb: "Senior Full Stack Developer with focus on AI & ML applications",
      githubUrl: "https://github.com/moneshvenkul/Logicmojo-data-science-projects",
      linkedinUrl: "https://www.linkedin.com/in/monesh-venkul-vommi-8a80b6174/"
    },
    {
      name: "Rishabh Gupta",
      username: "RishGupta",
      avatar: "assets/images/GithubImages/rishab.jpg",
      blurb: "Data Scientist & Analytics Mentor with experience in product ML",
      githubUrl: "https://github.com/RishGupta/LM-Sep-2025-projects",
      linkedinUrl: "https://www.linkedin.com/in/rishabhgupta96/"
    },
    {
      name: "Sourav Karmakar",
      username: "skarma91",
      avatar: "assets/images/GithubImages/Sourav.jpg",
      blurb: "Aspiring ML Engineer working on data science projects",
      githubUrl: "https://github.com/skarma91/logicmojo-data-science-april-2025/tree/main",
      linkedinUrl: "https://www.linkedin.com/in/sourav-karmakar-ds/"
    },
    {
      name: "Anitha Mani",
      username: "anitha05-ai",
      avatar: "assets/images/GithubImages/Anitha.jpg",
      blurb: "AI & Data Science enthusiast solving real-world problems",
      githubUrl: "https://github.com/anitha05-ai/Logicmojo-AI-DataScience-Assignments",
      linkedinUrl: "https://www.linkedin.com/in/anitha-mani-b104aa14/"
    },
    {
      name: "Manikandan B",
      username: "ManikandanB33",
      avatar: "assets/images/reviews/mani.jpg",
      blurb: "Machine Learning learner building hands-on projects",
      githubUrl: "https://github.com/ManikandanB33/Logicmojo-AI-DataScience-Assignments",
      linkedinUrl: "http://www.linkedin.com/in/manikandan-baskaran-34a090209"
    },
    {
      name: "Ujjwal Singh",
      username: "ujjwalsingh1067",
      avatar: "assets/images/reviews/ujwal.jpg",
      blurb: "Data Science student focused on AI & ML models",
      githubUrl: "https://github.com/ujjwalsingh1067/Logicmojo-AI-DataScience-Assignments",
      linkedinUrl: "https://www.linkedin.com/in/usf-ujjwalsingh/"
    },
    {
      name: "Sony Amancha",
      username: "amanchas",
      avatar: "assets/images/reviews/sony.jpg",
      blurb: "AI / ML enthusiast working through Logicmojo projects",
      githubUrl: "https://github.com/amanchas/-Logicmojo-AI-DataScience-Assignments",
      linkedinUrl: "https://www.linkedin.com/in/sony-amancha-a93543233?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app"
    },
    {
      name: "Surya Anirudh",
      username: "asuryaanirudh",
      avatar: "assets/images/GithubImages/surya.jpg",
      blurb: "Data Science practitioner exploring ML applications",
      githubUrl: "https://github.com/asuryaanirudh/MyProjects",
      linkedinUrl: "https://www.linkedin.com/in/surya-anirudh-annavajjala/"
    },
    {
      name: "Komala Shivanna",
      username: "KomalaML",
      avatar: "assets/images/GithubImages/Komala.jpg",
      blurb: "Passionate about AI/ML research and projects",
      githubUrl: "https://github.com/KomalaML/Logicmojo-AIML-Assignments-Komala",
      linkedinUrl: "https://www.linkedin.com/in/komala-shivanna-ph-d-31457222"
    },
    {
      name: "Brejesh Balakrishnan",
      username: "brej-29",
      avatar: "assets/images/GithubImages/Brejesh.jpg",
      blurb: "Building ML models for real-world challenges",
      githubUrl: "https://github.com/brej-29/Logicmojo-AIML-Assignments-DogBreedClassificationTensorFlow",
      linkedinUrl: "https://www.linkedin.com/in/brejesh-balakrishnan-7855051b9/"
    },
    {
      name: "Raja Seklin",
      username: "rajaseklin10",
      avatar: "assets/images/GithubImages/Raja.jpg",
      blurb: "Data Science learner solving assignments and projects",
      githubUrl: "https://github.com/rajaseklin10/Logicmojo-AI-DataScience-Assignments",
      linkedinUrl: "https://www.linkedin.com/in/raja-seklin-250285204/"
    },
    {
      name: "Anuj Khanna",
      username: "ajju1992",
      avatar: "assets/images/GithubImages/anuj.jpg",
      blurb: "AI & Data Science student working on end-to-end models",
      githubUrl: "https://github.com/ajju1992/Logicmojo-AI-DataScience-Assignments/tree/main",
      linkedinUrl: "https://www.linkedin.com/in/anuj-khanna-36a692b0/"
    },
    {
      name: "Velayutham Augustheesan",
      username: "velu333",
      avatar: "assets/images/GithubImages/Velayutham.jpg",
      blurb: "Machine Learning aspirant building foundational skills",
      githubUrl: "https://github.com/velu333/Logicmojo-AI-DataScience-Assignments",
      linkedinUrl: "http://www.linkedin.com/in/velayutham-augustheesan-60161918"
    },
    {
      name: "Umme Hani",
      username: "ummehani16519-ux",
      avatar: "assets/images/GithubImages/Umme.jpg",
      blurb: "Data & AI enthusiast doing hands-on learning",
      githubUrl: "https://github.com/ummehani16519-ux/Logicmojo-AI-DataScience-Assignments",
      linkedinUrl: "http://www.linkedin.com/in/umme-hani-44b87222b"
    },
    {
      name: "Sai Charan",
      username: "charan0396",
      avatar: "assets/images/GithubImages/sai.jpg",
      blurb: "Building skills in Data Science and ML",
      githubUrl: "https://github.com/charan0396/Logicmojo-AI-DataScience-Assignments",
      linkedinUrl: "https://www.linkedin.com/in/sai-charan-thota-883541180/"
    },
    {
      name: "Nitin Mathur",
      username: "nitinmathur",
      avatar: "assets/images/GithubImages/nitin.jpg",
      blurb: "Practicing ML with Python and end-to-end data science projects",
      githubUrl: "https://github.com/nitinmathur/Logicmojo-AI-ML-Sept25-nitinmathur",
      linkedinUrl: "https://www.linkedin.com/in/contactnitinmathur/"
    },
    {
      name: "Saurav Kumar Dey",
      username: "sauravdey99",
      avatar: "assets/images/GithubImages/sourav1.jpg",
      blurb: "Building machine learning models and evaluation workflows",
      githubUrl: "https://github.com/sauravdey99/Logicmojo-AI-ML-Sept25-sauravdey.git",
      linkedinUrl: "https://www.linkedin.com/in/saurav-kumar-dey-473691153/"
    },
    {
      name: "Fathima Sifa",
      username: "Fathimasifa2023",
      avatar: "assets/images/GithubImages/fathima.jpg",
      blurb: "Learning data science with Python, SQL, and applied ML",
      githubUrl: "https://github.com/Fathimasifa2023/LogicMojo_AI_ML_Sept25_FathimaSifa",
      linkedinUrl: "https://www.linkedin.com/in/fathima-musthafa-43b7791a7/"
    },
    {
      name: "Sateesh Narsingoju",
      username: "sateeshkn",
      avatar: "assets/images/GithubImages/sateesh.jpg",
      blurb: "Applying machine learning techniques to solve business problems",
      githubUrl: "https://github.com/sateeshkn/Logicmojo-AI-ML-Sept25-sateeshkn",
      linkedinUrl: "https://www.linkedin.com/in/sateeshkn/"
    },
    {
      name: "Sadananda RP",
      username: "SadanandaRP",
      avatar: "assets/images/GithubImages/sadananda.jpg",
      blurb: "Interested in predictive analytics, model tuning, and ML ops",
      githubUrl: "https://github.com/SadanandaRP/Logicmojo-AI-ML-Sept25-Sadananda",
      linkedinUrl: "https://www.linkedin.com/in/sadananda-rp-b3096652"
    },
    {
      name: "Aishwarya",
      username: "akathira",
      avatar: "assets/images/GithubImages/aishwarya.jpg",
      blurb: "Software developer upskilling in data science and machine learning",
      githubUrl: "https://github.com/akathira/LogicMojo_AI_ML_Sept25_aishwaryak",
      linkedinUrl: "https://www.linkedin.com/in/aishwarya-kathiravan-software-developer-chennai/"
    },
    {
      name: "Mukilan L S",
      username: "MukilanLS",
      avatar: "assets/images/GithubImages/mukilan.jpg",
      blurb: "Working on data preprocessing, feature engineering, and ML models",
      githubUrl: "https://github.com/MukilanLS/LogicMojo_AL_ML_Sep25_MukilanLS",
      linkedinUrl: "https://www.linkedin.com/in/mukilan-l-s-537655171/"
    },
    {
      name: "Sathishkumar Ramesh",
      username: "imsk12",
      avatar: "assets/images/GithubImages/sathish.jpg",
      blurb: "Exploring machine learning workflows and MLOps fundamentals",
      githubUrl: "https://github.com/imsk12/Logicmojo-AI-ML-Sept25-SathishkumarR",
      linkedinUrl: "https://www.linkedin.com/in/imsk12/"
    },
    {
      name: "Abhinav Bansal",
      username: "abhinavbansal89",
      avatar: "assets/images/GithubImages/abhinav.jpg",
      blurb: "Focused on supervised learning, analytics, and model evaluation",
      githubUrl: "https://github.com/abhinavbansal89/LogicMojo_AI_ML_Sept25_abhinavbansal",
      linkedinUrl: "https://www.linkedin.com/in/abhinav-bansal-425a7a15/"
    },
    {
      name: "Prashant Padekar",
      username: "prashantpadekar1",
      avatar: "assets/images/GithubImages/prashant.jpg",
      blurb: "Building ML pipelines and evaluating model performance",
      githubUrl: "https://github.com/prashantpadekar1/Logicmojo-AI-ML-Sept25-prashantpadekar",
      linkedinUrl: "https://www.linkedin.com/in/prashant-padekar-14a67931/"
    },
   // --- JAN 26 GITHUB STUDENTS (ADD THESE INSIDE `students` ARRAY) ---
{
  name: "Instructor (Suvam)",
  username: "SuvomShaw",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Suvom.jpg",
  blurb: "LogicMojo Data Science Instructor mentoring candidates for roles like Data Scientist, ML Engineer, Data Engineer, and AI Engineer.",
  githubUrl: "https://github.com/SuvomShaw/ai-mastery-bootcamp-curriculum/tree/main/LogicMojo-AI-ML-Jan26-suvom-01",
  linkedinUrl: "https://www.linkedin.com/in/suvomshaw/"
},
{
  name: "Pravash",
  username: "pravash522",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Pravash.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building hands-on assignments.",
  githubUrl: "https://github.com/pravash522/LogicMojo-AI-ML-Jan26/tree/main/LogicMojo-AI-ML-Jan26-PRAVASHQuantam/assignments",
  linkedinUrl: "https://www.linkedin.com/in/pravashpati-pramanik-123b36264"
},
{
  name: "Sulaiman",
  username: "SLTaiwo",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Sulaiman.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building projects and assignments.",
  githubUrl: "https://github.com/SLTaiwo/Data-Science-AI-ML/tree/main/LogicMojo-AI-ML-Jan26-STaiwo",
  linkedinUrl: "https://www.linkedin.com/in/sulaimantaiwo/"
},
{
  name: "Shreya Saraf",
  username: "Shreya1619",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Shreya Saraf.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on portfolio projects.",
  githubUrl: "https://github.com/Shreya1619/LogicMojo-AI-ML-Jan26-ShreyaSaraf",
  linkedinUrl: "https://www.linkedin.com/in/shreyasaraf"
},
{
  name: "Akshith",
  username: "akshithreddy502",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Akshith.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building a job-ready portfolio.",
  githubUrl: "https://github.com/akshithreddy502/LogicMojo-AI-ML-Jan26-Akshith",
  linkedinUrl: "https://www.linkedin.com/in/akshithreddy27"
},
{
  name: "Avinash Singh",
  username: "avi17098",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) practicing with assignments.",
  githubUrl: "https://github.com/avi17098/LogicMojo-AI-ML-Jan26-Avinash",
  linkedinUrl: ""
},
{
  name: "Anjali Thakkar",
  username: "anji2008thkr2",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building hands-on projects.",
  githubUrl: "https://github.com/anji2008thkr2/LogicMojo-AI-ML-Jan26-Anjali.git",
  linkedinUrl: "https://www.linkedin.com/in/anjali-thakkar-245050290"
},
{
  name: "Reetha Rajagopal",
  username: "reetharaj20-star",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Reetha Rajagopal.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building course projects.",
  githubUrl: "https://github.com/reetharaj20-star/LogicMojo-AI-ML-Jan26-Reetha.git",
  linkedinUrl: "https://www.linkedin.com/in/reetha-rajagopal-57b96b213/"
},
{
  name: "Rishiraj Singh",
  username: "Rishiraj1994",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Rishiraj Singh.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building end-to-end projects.",
  githubUrl: "https://github.com/Rishiraj1994/LogicMojo-AI-ML-Jan26-rishiraj19",
  linkedinUrl: "https://www.linkedin.com/in/rishiraj--singh/"
},
{
  name: "Shweta",
  username: "shweta1503tech",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working through assignments.",
  githubUrl: "https://github.com/shweta1503tech/LogicMojo-AI-ML-Jan26-Shweta1503",
  linkedinUrl: "https://www.linkedin.com/in/shweta-amin-03668118"
},
{
  name: "Ichwan",
  username: "isuchan",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Ichwan.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building projects.",
  githubUrl: "https://github.com/isuchan/logicmojo-ai-ml",
  linkedinUrl: "https://www.linkedin.com/in/ichwan-chandra/"
},
{
  name: "Tanisha",
  username: "teakoko68",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on assignments.",
  githubUrl: "https://github.com/teakoko68/LogicMojo-AI-ML-Jan26-tanisha",
  linkedinUrl: "https://www.linkedin.com/in/tanishad4260/"
},
{
  name: "Dilshad Hussain",
  username: "Dilshad13",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building practice projects.",
  githubUrl: "https://github.com/Dilshad13/ai_ml_jan26",
  linkedinUrl: "https://www.linkedin.com/in/dilshad-hussain-b6266926/"
},
{
  name: "Sagar Darbarwar",
  username: "sagardarbarwar",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Sagar Darbarwar.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building course projects.",
  githubUrl: "https://github.com/sagardarbarwar/LogicMojo-AI-ML-Jan26-Sagar.git",
  linkedinUrl: "https://www.linkedin.com/in/sagar-darbarwar/"
},
{
  name: "Leah",
  username: "leahwong",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Leah.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on assignments.",
  githubUrl: "https://github.com/leahwong/LogicMojo-AI-ML-Jan26-leahwns",
  linkedinUrl: "https://www.linkedin.com/in/ngaisinwong/"
},
{
  name: "Srikrishna Karatalapu",
  username: "SriKaratalapu",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Srikrishna Karatalapu.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building portfolio projects.",
  githubUrl: "https://github.com/SriKaratalapu/LogicMojo-AI-ML-Jan26-srikaratalapu",
  linkedinUrl: "https://www.linkedin.com/in/srikrishna-karatalapu/"
},
{
  name: "Anoop P S",
  username: "AnoopPS02",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Anoop P S.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on projects.",
  githubUrl: "https://github.com/AnoopPS02/LogicMojo-AI-ML-Jan26-anoop",
  linkedinUrl: "https://www.linkedin.com/in/anoop-p-s-900580166/"
},
{
  name: "Shanthan Reddy",
  username: "Shanty-Dangerzone",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Shanthan Reddy.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building course projects.",
  githubUrl: "https://github.com/Shanty-Dangerzone/LogicMojo-AI-ML-Jan26-ShanthanReddy",
  linkedinUrl: "https://www.linkedin.com/in/shanthan-reddy-4496b420/"
},
{
  name: "Dheeraj Singh",
  username: "dheeraj0032scm",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Dheeraj Singh.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) contributing via commits and practice.",
  githubUrl: "https://github.com/dheeraj0032scm/logicmojo/commits?author=dheeraj0032scm",
  linkedinUrl: "https://www.linkedin.com/in/dheeraj-singhscm"
},
{
  name: "Manobala Surulichamy",
  username: "manobalatester",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on assignments.",
  githubUrl: "https://github.com/manobalatester/LogicMojo-AI-ML-Jan26-Manobala",
  linkedinUrl: "https://www.linkedin.com/in/manobala-surulichamy/"
},
{
  name: "Ganesh Prasad",
  username: "PrasadGanesh",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Ganesh Prasad.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building assignments and projects.",
  githubUrl: "https://github.com/PrasadGanesh/LogicMojo-AI-ML-Jan26/tree/main/LogicMojo-AI-ML-Jan26-Ganesh227",
  linkedinUrl: "https://www.linkedin.com/in/ganesh-prasad/"
},
{
  name: "Raikamal Mukherjee",
  username: "Raikamal-Mukherjee",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on course projects.",
  githubUrl: "https://github.com/Raikamal-Mukherjee/LogicMojo-AI-ML-Jan26-raikamal",
  linkedinUrl: "https://www.linkedin.com/in/raikamal-mukherjee-66238111b/"
},
{
  name: "Yaswanth Reddy kakunuri",
  username: "yaswanth222",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Yaswanth Reddy kakunuri.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building portfolio projects.",
  githubUrl: "https://github.com/yaswanth222/LogicMojo-AI-ML-Jan26-Yaswanth",
  linkedinUrl: "https://www.linkedin.com/in/yaswanth-reddy-kakunuri-98a09b125"
},
{
  name: "Lokesh Patel",
  username: "lokipatel",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Lokesh Patel.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on assignments.",
  githubUrl: "https://github.com/lokipatel/LogicMojo-AI-ML-Jan26-Lok345",
  linkedinUrl: "https://www.linkedin.com/in/lopatel123/"
},
{
  name: "Vaibhav Tiwari",
  username: "vaitiwari",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Vaibhav Tiwari.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building course projects.",
  githubUrl: "https://github.com/vaitiwari/LogicMojo-AI-ML-Jan26-vaibhav-ti.git",
  linkedinUrl: "https://www.linkedin.com/in/vaibhav-ti/"
},
{
  name: "Sreevani Rayavaram",
  username: "sreevani916",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on assignments.",
  githubUrl: "https://github.com/sreevani916/LogicMojo-AI-ML-Jan26-SreevaniR/tree/main",
  linkedinUrl: "https://www.linkedin.com/in/sreevani-rayavaram-64372011b/"
},
{
  name: "Rakshith Hegde",
  username: "hegderr",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building hands-on projects.",
  githubUrl: "https://github.com/hegderr/LogicMojo-AI-ML-Jan26-Rakshith.git",
  linkedinUrl: "https://www.linkedin.com/in/rakshith-hegde-38a43122/"
},
{
  name: "Mohammed Kashif",
  username: "Kashif-Atom",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Mohammed Kashif.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on course projects.",
  githubUrl: "https://github.com/Kashif-Atom/LogicMojo-AI-ML-Jan26-Kashif.git",
  linkedinUrl: "https://www.linkedin.com/in/mohammed-kashif-6238b81a8/"
},
{
  name: "Chandhrramohan Rajan",
  username: "CRajan",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building assignments.",
  githubUrl: "https://github.com/CRajan/Data-Science-AI-ML/tree/main/LogicMojo-AI-ML-Jan26-CRajan",
  linkedinUrl: "https://www.linkedin.com/in/chandrarajan/"
},
{
  name: "Sreejith.C",
  username: "sreeoojit",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Sreejith.C.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on projects.",
  githubUrl: "https://github.com/sreeoojit/PY/blob/main/LogicMojo-AI-ML-Jan26/sc",
  linkedinUrl: "https://www.linkedin.com/in/csreejith/"
},
{
  name: "Swati Tiwari",
  username: "SWATI456-coder",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Swati Tiwari.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building course projects.",
  githubUrl: "https://github.com/SWATI456-coder/LogicMojo-AI-ML-Jan26-Swati.git",
  linkedinUrl: "https://www.linkedin.com/in/tiwari-swati"
},
{
  name: "Vedant Dadhich",
  username: "Ved26",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Vedant Dadhich.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on assignments.",
  githubUrl: "https://github.com/Ved26/LogicMojo-AI-ML-Jan26-Ved26",
  linkedinUrl: "https://www.linkedin.com/in/vedant-dadhich-376a79149"
},
{
  name: "Shivam Saxena",
  username: "shankeysaxena",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Shivam Saxena.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building projects.",
  githubUrl: "https://github.com/shankeysaxena/LogicMojo-AI-ML-Jan26-Shivam",
  linkedinUrl: ""
},
{
  name: "Sameer Tandon",
  username: "tandonsameer",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Sameer Tandon.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on course projects.",
  githubUrl: "https://github.com/tandonsameer/LogicMojo-AI-ML-Jan26-satan",
  linkedinUrl: ""
},
{
  name: "Bhupesh Vipparla",
  username: "BhupeshVipparla",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Bhupesh Vipparla.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building assignments and projects.",
  githubUrl: "https://github.com/BhupeshVipparla/LogicMojo-AI-ML-Jan26-bhupesh",
  linkedinUrl: "https://www.linkedin.com/in/bhupesh-8652"
},
{
  name: "Soujanya Karatalapu",
  username: "skaratalapu",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on assignments.",
  githubUrl: "https://github.com/skaratalapu/LogicMojo-AI-ML-Jan26-SoujanyaKaratalapu",
  linkedinUrl: "https://www.linkedin.com/in/soujanya-karatalapu-5226773b/"
},
{
  name: "Aditya",
  username: "adityagitdev",
  avatar: "",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building course projects.",
  githubUrl: "https://github.com/adityagitdev/LogicMojo-AI-ML-Jan26-Aditya",
  linkedinUrl: ""
},
{
  name: "Venkataraman Sethuraman",
  username: "venkat6631",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Venkataraman Sethuraman.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on assignments.",
  githubUrl: "https://github.com/venkat6631/LogicMojo-AI-ML-Jan26-thisisvenkat",
  linkedinUrl: "https://www.linkedin.com/in/thisisvenkatt"
},
{
  name: "Vinay Kumar Tokala",
  username: "vinaykumartokalalearning-png",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Vinay Kumar Tokala.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building projects.",
  githubUrl: "https://github.com/vinaykumartokalalearning-png/LogicMojo-AI-ML-Jan26-VKT",
  linkedinUrl: "https://www.linkedin.com/in/vinay-kumar-tokala-692826180/"
},
{
  name: "Chinmay Garg",
  username: "Chinmay50",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Chinmay Garg.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) working on course projects.",
  githubUrl: "https://github.com/Chinmay50/LogicMojo-AI-ML-Jan26-Chinmay50",
  linkedinUrl: "https://www.linkedin.com/in/chinmay-garg-244b1a118/"
},
{
  name: "Shravya Errabelly",
  username: "shravyraoe-lab",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Shravya Errabelly.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building assignments.",
  githubUrl: "https://github.com/shravyraoe-lab/LogicMojo-AI-ML-Jan26-shravya123.git",
  linkedinUrl: ""
},
{
  name: "Parul Rawat",
  username: "forgerlab",
  avatar: "https://logicmojo.com/assets/dist/new_index_design/images/GithubJan26/Parul Rawat.jpg",
  blurb: "LogicMojo Data Science Candidate (aspiring Data Scientist / ML Engineer / Data Engineer / AI Engineer) building hands-on projects.",
  githubUrl: "https://github.com/forgerlab/AI-Playground",
  linkedinUrl: "https://www.linkedin.com/in/parul-rawat/"
},

    
    // --- NEWLY ADDED STUDENTS ---
    {
      name: "Zachari Bultman",
      username: "SundayKoi",
      avatar: "https://media.licdn.com/dms/image/v2/D5635AQEi8asfJSoLCQ/profile-framedphoto-shrink_400_400/profile-framedphoto-shrink_400_400/0/1691017512586?e=1767510000&v=beta&t=opw7dvnBWozjo3-3rdSWeb9b9RglmO-2K8terE23ZNg",
      githubUrl: "https://github.com/SundayKoi/logicmojo-ds-ai-nov-2025-Zachari",
      linkedinUrl: "https://www.linkedin.com/in/zachari-bultman-083b47113",
      blurb: getBlurb(0)
    },
    {
      name: "Sumana Khan",
      username: "Sumanabec",
      avatar: "https://media.licdn.com/dms/image/v2/C4E03AQEMC_gEmrEfTw/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1517034099926?e=1768435200&v=beta&t=9LdEiSNNMDhySmWjTsWSx1QPsfOqm6MJLZi7uUJ-NnM",
      githubUrl: "https://github.com/Sumanabec/DataScience-AI-ML-2025-Sumana",
      linkedinUrl: "https://www.linkedin.com/in/sumana-khan-4aa242a1",
      blurb: getBlurb(1)
    },
    {
      name: "Sri Nikhitha K",
      username: "nikkisrepos",
      avatar: "https://media.licdn.com/dms/image/v2/D5603AQEHQ-P5YtPj-Q/profile-displayphoto-scale_400_400/B56ZpU8LraJYAg-/0/1762361656131?e=1768435200&v=beta&t=EkOX_u0De9jL6cs4SSE2HSwl6NI7xO0StqpnvT1FMLM",
      githubUrl: "https://github.com/nikkisrepos/AI-ML-Nov25-srinikhitha",
      linkedinUrl: "https://www.linkedin.com/in/sai-sri-nikhitha-k-37a62a248/",
      blurb: getBlurb(2)
    },
    {
      name: "Govardhan",
      username: "GovardhanGova7277",
      avatar: "https://media.licdn.com/dms/image/v2/D5603AQFPfVOEcVF6lA/profile-displayphoto-shrink_400_400/B56ZZ1crYYGQAk-/0/1745727176924?e=1768435200&v=beta&t=02C3qH4oIl0nKZWR6IaJmTBPndolDix2YpNBW5mhy9o",
      githubUrl: "https://github.com/GovardhanGova7277/AI",
      linkedinUrl: "https://www.linkedin.com/in/govardhang",
      blurb: getBlurb(3)
    },
    {
      name: "Arunkumar K",
      username: "arunKumar0816",
      avatar: "https://media.licdn.com/dms/image/v2/D5635AQFtTyGAfbu2MQ/profile-framedphoto-shrink_400_400/B56Zj0KYIjHQAc-/0/1756443018123?e=1767510000&v=beta&t=fXKbSSWm3TGhL6_dp98cJbQQZLjDVyPOQDAzXMpXggU",
      githubUrl: "https://github.com/arunKumar0816",
      linkedinUrl: "https://www.linkedin.com/in/arun-kumar-9bb5a617b/",
      blurb: getBlurb(4)
    },
    {
      name: "Isra Osman",
      username: "IsraOsman",
      avatar: "https://media.licdn.com/dms/image/v2/C4E03AQG2eojP2UYxFQ/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1633058014334?e=1768435200&v=beta&t=FTO1AZNP4ffPX5saQXZEY3LK5suXRUOoAQJESOduL98",
      githubUrl: "https://github.com/IsraOsman/logicmojo-data-science-ai-nov-2025-Isra-O",
      linkedinUrl: "https://www.linkedin.com/in/isra-aa-osman/",
      blurb: getBlurb(5)
    },
    {
      name: "Alok Das",
      username: "Alokdas09",
      avatar: "https://media.licdn.com/dms/image/v2/D5603AQEzJjal270oWw/profile-displayphoto-scale_400_400/B56ZsIAZFPGsAg-/0/1765365880070?e=1768435200&v=beta&t=bRnZXFdMCHI9oTi6ejGMzLOYkBHFYjLecR772H-ZFic",
      githubUrl: "https://github.com/Alokdas09/LogicMojo_Alok",
      linkedinUrl: "https://www.linkedin.com/in/alok-das-88776a3a/",
      blurb: getBlurb(6)
    },
    {
      name: "Ayan Dey",
      username: "ayanseeu",
      avatar: "https://media.licdn.com/dms/image/v2/C4E03AQEdsTPWN1271Q/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1516639548402?e=1768435200&v=beta&t=to47OqvyTv_sN-qfh6qp6he0a-vQmNHMtlsPn9Sk7TM",
      githubUrl: "https://github.com/ayanseeu/LogicMojo-AI-ML-Novt25-Ayan-Dey",
      linkedinUrl: "https://www.linkedin.com/in/ayan-d1/",
      blurb: getBlurb(7)
    },
    {
      name: "Swathi S",
      username: "SwathiAIML12",
      avatar:"",
      githubUrl: "https://github.com/SwathiAIML12/LogicMojo-AI-ML-Sept25-Swathi",
      linkedinUrl: "https://www.linkedin.com/in/swathi-s-aa3b2623a/",
      blurb: getBlurb(8)
    },
    {
      name: "Hamed Sanusi",
      username: "shoptsc",
      avatar: "https://media.licdn.com/dms/image/v2/D4E03AQE9vY5DKYh7FA/profile-displayphoto-scale_400_400/B4EZqPtqTGJgAg-/0/1763347705288?e=1768435200&v=beta&t=9bF4qUWwaMNMe3ygPegamFmvgx_V9nhU1g1TElLBfiA",
      githubUrl: "https://github.com/shoptsc/logicmojo_ds_ai_hamed_sanusi",
      linkedinUrl: "https://www.linkedin.com/in/hamed-sanusi/",
      blurb: getBlurb(9)
    },
    {
      name: "Mukul Rastogi",
      username: "mukulrastogi-96",
      avatar: "https://media.licdn.com/dms/image/v2/D5603AQFvbyze6I2dMw/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1726765795200?e=1768435200&v=beta&t=8wXDvkjQj-DAwiJ4AxFBjOtWZJ2JJvSZp_myUlSKDHk",
      githubUrl: "https://github.com/mukulrastogi-96/AI-ML-Nov25-MukulRastogi",
      linkedinUrl: "https://www.linkedin.com/in/mukulrastogi96/",
      blurb: getBlurb(10)
    },
    {
      name: "Pradyum Reddy Gade",
      username: "pradyumrg21",
      avatar:"",
      githubUrl: "https://github.com/pradyumrg21/LogicMojo-AI-ML-Nov25-pradyumrg21.git",
      linkedinUrl: "", 
      blurb: getBlurb(11)
    },
    {
      name: "Sudhakar Sharma",
      username: "sudhakar-pixel",
      avatar: "https://media.licdn.com/dms/image/v2/D5635AQHb52Al07sr6Q/profile-framedphoto-shrink_400_400/B56ZmB9S4MJoAg-/0/1758821953427?e=1767510000&v=beta&t=DLBy7ed79AonuPjey7C_3T--h4ZVZQx_5v3f5rERPCU",
      githubUrl: "https://github.com/sudhakar-pixel/DS-AI-ML-Sud-Nov25",
      linkedinUrl: "https://www.linkedin.com/in/sudhakar-sharma-00832991",
      blurb: getBlurb(12)
    },
    {
      name: "Mayank Chaudhari",
      username: "Mayank-Chaudhari9",
      avatar: "https://media.licdn.com/dms/image/v2/D5635AQHb52Al07sr6Q/profile-framedphoto-shrink_400_400/B56ZmB9S4MJoAg-/0/1758821953427?e=1767510000&v=beta&t=DLBy7ed79AonuPjey7C_3T--h4ZVZQx_5v3f5rERPCU",
      githubUrl: "https://github.com/Mayank-Chaudhari9/AI-ML-Nov25-Mayank",
      linkedinUrl: "https://www.linkedin.com/in/mayankchaudhari9/",
      blurb: getBlurb(13)
    },
    {
      name: "Shilpa Gangadhara",
      username: "shilpa-gangadhara",
      avatar:"",
      githubUrl: "https://github.com/shilpa-gangadhara/logicmojo-ai-ml-course",
      linkedinUrl: "https://www.linkedin.com/in/shilpagangadhara-2953615b",
      blurb: getBlurb(14)
    },
    {
      name: "Aditya Raj Anand",
      username: "Gud-Engineer",
      avatar: "https://media.licdn.com/dms/image/v2/D4D03AQEwN8tweLdgbA/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1687346578315?e=1768435200&v=beta&t=QsUPVa9HRZBB0Y5hQTYIBUgO-ateHelLjU7qoroEufc",
      githubUrl: "https://github.com/Gud-Engineer/data-science-ai-learning-log/tree/main/Logicmojo-AI-ML-Sept25-adityarajanand",
      linkedinUrl: "https://www.linkedin.com/in/aditya-raj-anand-transforming-data/",
      blurb: getBlurb(15)
    },
    {
      name: "Manasask",
      username: "mskstudio31",
      avatar:"",
      githubUrl: "https://github.com/mskstudio31-jpg/LogicMojo-AI-ML-Nov25-manasask",
      linkedinUrl: "https://www.linkedin.com/in/manasa-sk-83726723/",
      blurb: getBlurb(16)
    },
    {
      name: "Nabin Adhikari",
      username: "nabinadhikari",
      avatar: "https://media.licdn.com/dms/image/v2/C5603AQHUcoI3a3GLzw/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1605653388153?e=1768435200&v=beta&t=gvXCknJkfe6xjj2jyMyD7RcRbZHGAtTO7LbTlJwYOOM",
      githubUrl: "https://github.com/nabinadhikari/LogicMojo-AI-ML-Sept25-Nabin-Adhikari",
      linkedinUrl: "https://www.linkedin.com/in/nabinadhikari/",
      blurb: getBlurb(17)
    },
    {
      name: "Sakshi Mathur",
      username: "smathur89",
      avatar: "https://media.licdn.com/dms/image/v2/D5603AQF_XA8Piguttg/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1702840142517?e=1768435200&v=beta&t=GsLPSJHeslyX_H2kTWHZkT78Js-KqxiJgEkkDwRU_Vo",
      githubUrl: "https://github.com/smathur89/LogicMojo-AI-ML-Nov25--Sakshi.Mathur-",
      linkedinUrl: "https://www.linkedin.com/in/sakshi-mathur-7a645277/",
      blurb: getBlurb(18)
    },
    {
      name: "Nikhil S",
      username: "nikhilnani532",
      avatar:"",
      githubUrl: "https://github.com/nikhilnani532/DS-AI.git",
      linkedinUrl: "",
      blurb: getBlurb(19)
    },
    {
      name: "Tod",
      username: "starbaseai",
      avatar:"",
      githubUrl: "https://github.com/starbaseai/ds-ai",
      linkedinUrl: "",
      blurb: getBlurb(20)
    },
    {
      name: "Neena Abraham",
      username: "neenaabraham",
      avatar:"",
      githubUrl: "https://github.com/neenaabraham/AI-ML-Practice-Assignments",
      linkedinUrl: "https://www.linkedin.com/in/neena-abraham-bb2b90213",
      blurb: getBlurb(21)
    },
    {
      name: "Kameshwari S",
      username: "preethkams",
      avatar: "https://media.licdn.com/dms/image/v2/D5603AQEjK5LRvCpVtQ/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1726581006003?e=1768435200&v=beta&t=zxAq-t9BswqhGxvEjg75NWZ3i0ys4a0pTFJoAKOmoAw",
      githubUrl: "https://github.com/preethkams/AIML2025_Kameshwari",
      linkedinUrl: "https://www.linkedin.com/in/kameshwari-sivakumar-3599343b/",
      blurb: getBlurb(22)
    },
    {
      name: "Abdulsalam Jamiu",
      username: "abdulsalamjamiu89",
      avatar:"",
      githubUrl: "https://github.com/abdulsalamjamiu89-bit/LogicMojo-AI-ML-Sept25-abdulsalamjamiu89",
      linkedinUrl: "https://www.linkedin.com/in/abdulsalam-jamiu-mohammed-b94362124/",
      blurb: getBlurb(23)
    },
    {
      name: "Manoj Kumar",
      username: "manojrasaily",
      avatar: "https://media.licdn.com/dms/image/v2/C4D03AQGQz0JwzhPC2Q/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1622226657357?e=1768435200&v=beta&t=rO5a_JIGP4voyoi5EADDCyCNWKKKCwrWhUll3BfcodE",
      githubUrl: "https://github.com/manojrasaily?tab=repositories&q=&type=public&sort=",
      linkedinUrl: "https://www.linkedin.com/in/manoj-kumar-21a61b205",
      blurb: getBlurb(24)
    },
    {
      name: "Nikhil Venugopal",
      username: "atvnikhil",
      avatar: "https://media.licdn.com/dms/image/v2/C5603AQEja_gCWYiWhg/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1612192916286?e=1768435200&v=beta&t=uooXHQgttPgkOm3s8O1XsQIinwi38ElJmt9EAI1Z-zg",
      githubUrl: "https://github.com/atvnikhil",
      linkedinUrl: "https://www.linkedin.com/in/nikhil-venugopal-000",
      blurb: getBlurb(25)
    },
    {
      name: "Sandeep HJ",
      username: "SANDEEPHJ",
      avatar: "https://media.licdn.com/dms/image/v2/D4E03AQHvkgT1xaWksw/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1712633088241?e=1768435200&v=beta&t=EIUdKbzddAaMSDy4unaBL9QhIoZ-tjNxJQYwcDn4cdQ",
      githubUrl: "https://github.com/SANDEEPHJ/LOGICMOJO-DS-AI-COURSE",
      linkedinUrl: "https://www.linkedin.com/in/sandeephj/",
      blurb: getBlurb(26)
    },
    {
      name: "Madhusri CR",
      username: "madhusri444",
      avatar:"",
      githubUrl: "https://github.com/madhusri444/LogicMojo-AI-ML-Sept25-Madhusri",
      linkedinUrl: "https://www.linkedin.com/in/madhusricr",
      blurb: getBlurb(27)
    },
    {
      name: "Kannappar P",
      username: "kannappar",
      avatar: "https://media.licdn.com/dms/image/v2/D5635AQH-zNP3EemOZA/profile-framedphoto-shrink_400_400/profile-framedphoto-shrink_400_400/0/1706726425451?e=1767510000&v=beta&t=lQjB2OB0uuU4cvBcGhNzm3lvb4efVhXlZFtIXk4iPr8",
      githubUrl: "https://github.com/kannappar/LogicMojo-AI-ML-Nov25-kannappar",
      linkedinUrl: "https://www.linkedin.com/in/kannappar-panmozhi-aa5217a1/",
      blurb: getBlurb(28)
    },
    {
      name: "Swapnil Jha",
      username: "Swapnil0506",
      avatar: "https://media.licdn.com/dms/image/v2/D5635AQHh5n41tZRCsQ/profile-framedphoto-shrink_400_400/B56Zn3aJvfHQAg-/0/1760792452394?e=1767510000&v=beta&t=NPkpwbtQ6vOCzmS42dPBE8ktQSSr7vJ1pW9wiplFwgs",
      githubUrl: "https://github.com/Swapnil0506/DS-AI-Practice",
      linkedinUrl: "https://www.linkedin.com/in/swapnil-jha-b21bb3197/",
      blurb: getBlurb(29)
    },
    {
      name: "Vineeth Reddy",
      username: "VineethReddyBillam",
      avatar: "https://media.licdn.com/dms/image/v2/D5603AQHp3K9sgriaXQ/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1720823677022?e=1768435200&v=beta&t=NYZe52RX6TOrKkrADyixHQlfl82tDZJEvKHViFqm0V0",
      githubUrl: "https://github.com/VineethReddyBillam/Logicmojo_25",
      linkedinUrl: "https://www.linkedin.com/in/vineeth-reddy-billam/",
      blurb: getBlurb(30)
    },
    {
      name: "Suraj Sharma",
      username: "surajsharma286",
      avatar: "https://media.licdn.com/dms/image/v2/C4D03AQFFntFhXFRysQ/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1621411365297?e=1768435200&v=beta&t=tFo41m0r-1J-qtC_3GTFXHKam1sg0RMfIDQ31ce89dk",
      githubUrl: "https://github.com/surajsharma286/data-science-ai/tree/main",
      linkedinUrl: "https://www.linkedin.com/in/surajsharma286/",
      blurb: getBlurb(31)
    },
    {
      name: "Sulochana Jayakumar",
      username: "sulochanammr",
      avatar:"",
      githubUrl: "https://github.com/sulochanammr/logicmojo-data-science-ai-nov-2025",
      linkedinUrl: "", 
      blurb: getBlurb(32)
    },
    {
      name: "Akansha Tiwari",
      username: "minty123467",
      avatar: "https://media.licdn.com/dms/image/v2/C4D03AQF3RY4vNjgqQw/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1662795733226?e=1768435200&v=beta&t=xV7OUeun2rq_51TBOOdNlrmD9mqDNapBYuog3ZOfNTk",
      githubUrl: "https://github.com/minty123467/logicmojo-data-science-ai-nov-2025",
      linkedinUrl: "https://www.linkedin.com/in/akansha-tiwari-199508118/",
      blurb: getBlurb(33)
    },
    {
      name: "Akshay Dornala",
      username: "AkshayReddy777",
      avatar: "https://media.licdn.com/dms/image/v2/C4D03AQE4pYdbactQaw/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1640019652726?e=1768435200&v=beta&t=y49lyKFX6xTqbT2L86FUxSks0c3OWBAzdPP3aeduJe4",
      githubUrl: "https://github.com/AkshayReddy777/logicmojo-data-science-ai-nov-2025.git",
      linkedinUrl: "https://www.linkedin.com/in/akshay-dornala/",
      blurb: getBlurb(34)
    },
    {
      name: "Adekunle Bakare",
      username: "battoyyeb",
      avatar: "https://media.licdn.com/dms/image/v2/D5603AQEvVXz_235fBA/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1697238062557?e=1768435200&v=beta&t=RfXexMHANTg6kajB-iIzXyHHySRN3qYKdKjbWu3J2nY",
      githubUrl: "https://github.com/battoyyeb/logicmojo_AI_ML_Nov25_Adekunle.git",
      linkedinUrl: "https://www.linkedin.com/in/adekunle-bakare-20b200aa/",
      blurb: getBlurb(35)
    },
    {
      name: "Nithin Kumar Allam",
      username: "Nithin3636",
      avatar: "https://media.licdn.com/dms/image/v2/D5635AQFkdwRDBkRy9g/profile-framedphoto-shrink_400_400/B56Zr1V98OJsAk-/0/1765052768581?e=1767510000&v=beta&t=RH1X4XruGWqi6hvOx0fxe09YOYqe0HWdNWgWqZtqbT8",
      githubUrl: "https://github.com/Nithin3636/logicmojo-data-science-ai-nov-2025",
      linkedinUrl: "https://www.linkedin.com/in/nithin-allam/",
      blurb: getBlurb(36)
    },
    {
      name: "Dilip Singh",
      username: "dilipasingh",
      avatar:"",
      githubUrl: "https://github.com/dilipasingh/logicmojo-data-science-ai-nov-2025",
      linkedinUrl: "https://www.linkedin.com/in/dilip-a-singh/",
      blurb: getBlurb(37)
    },
    {
      name: "Deepika Sridhar",
      username: "DeepikaSridhar2112",
      avatar: "https://media.licdn.com/dms/image/v2/D5603AQG9QOVtf_9mKQ/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1702095396814?e=1768435200&v=beta&t=J9pwuqZEjcfius0bkFzIFFFCKtLwcGZFeLTnvnU7y0E",
      githubUrl: "https://github.com/DeepikaSridhar2112/logicmojo-data-science-ai-nov-2025",
      linkedinUrl: "https://www.linkedin.com/in/deepika-sridhar-02987720a/",
      blurb: getBlurb(38)
    },
    {
      name: "Annu Jose",
      username: "AnnuJose1998",
      avatar: "https://media.licdn.com/dms/image/v2/D4D03AQFyuz9AHpqiLg/profile-displayphoto-scale_400_400/B4DZfgGYC.GsAk-/0/1751811458896?e=1768435200&v=beta&t=KEF5OcLtTvRQGHLHX2rMYxjh8uaDVafab9Wckc_7NbU",
      githubUrl: "https://github.com/AnnuJose1998/LogicMojo-AI-ML-Sept25--AnnuJose-",
      linkedinUrl: "https://www.linkedin.com/in/annu-jose01-dataanalyst",
      blurb: getBlurb(39)
    },
    {
      name: "Diptendu kumar kundu",
      username: "dkkundu90",
      avatar: "https://media.licdn.com/dms/image/v2/D4D03AQFVvq_qOG1PBQ/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1667596130909?e=1768435200&v=beta&t=IjWkKC8L0Gjv_8t9GXFPuV6oFGwXIovU2us38yUxyj0",
      githubUrl: "https://github.com/dkkundu90/LogicMojo-AI-ML-Nov25-Diptedu-kumar-kundu",
      linkedinUrl: "https://www.linkedin.com/in/diptendu-kumar-kundu-b9514a147/",
      blurb: getBlurb(40)
    },
    {
      name: "Sourabh Jha",
      username: "sourabhjha3010",
      avatar: "https://media.licdn.com/dms/image/v2/D4D03AQFoepfFbSjlZQ/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1725270939059?e=1768435200&v=beta&t=6v1V_BVK125-0Y3MMikaqWtydPpdjTN4AdnV907ERTQ",
      githubUrl: "https://github.com/sourabhjha3010/Data-Science-AI-ML-2025-Sourabh",
      linkedinUrl: "https://www.linkedin.com/in/sourabh-jha-1117098/",
      blurb: getBlurb(41)
    }
  ];

  const firstRow = students.slice(0, Math.ceil(students.length / 2));
  const secondRow = students.slice(Math.ceil(students.length / 2));

  return (
    <div className="logicmojo-premium-container">
      {/* --- INLINE STYLES FOR PREMIUM LOOK --- */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .logicmojo-premium-container {
          --bg-main: #f8fafc;
          --bg-pattern: #f1f5f9;
          --text-primary: #0f172a;
          --text-secondary: #475569;
          --primary-color: #4f46e5;
          --primary-glow: rgba(79, 70, 229, 0.4);
          --github-color: #24292e;
          --linkedin-color: #0077b5;
          --card-bg: #ffffff;
          --font-family: 'Plus Jakarta Sans', sans-serif;
          
          font-family: var(--font-family);
          background-color: var(--bg-main);
          color: var(--text-primary);
          overflow-x: hidden;
          width: 100%;
        }

        /* --- HERO SECTION --- */
        .premium-hero {
          position: relative;
          padding: 80px 20px 60px;
          text-align: center;
          background: radial-gradient(circle at 50% 0%, #eef2ff 0%, transparent 70%);
          overflow: hidden;
        }

        .hero-bg-grid {
          position: absolute;
          inset: 0;
          background-image: linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px);
          background-size: 40px 40px;
          opacity: 0.3;
          mask-image: linear-gradient(180deg, black, transparent);
          z-index: 0;
        }

        .hero-content {
          position: relative;
          z-index: 10;
          max-width: 800px;
          margin: 0 auto;
        }

        .icon-box {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #4f46e5, #818cf8);
          border-radius: 24px;
          box-shadow: 0 20px 40px -10px var(--primary-glow);
          margin-bottom: 30px;
          animation: float 6s ease-in-out infinite;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: -0.03em;
          margin-bottom: 20px;
          background: linear-gradient(to right, #1e293b, #334155);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .highlight-text {
          background: linear-gradient(135deg, #4f46e5 0%, #ec4899 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hero-desc {
          font-size: 1.125rem;
          line-height: 1.7;
          color: var(--text-secondary);
          max-width: 600px;
          margin: 0 auto 40px;
        }

        /* --- STATS --- */
        .stats-row {
          display: flex;
          justify-content: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .stat-pill {
          background: white;
          padding: 12px 24px;
          border-radius: 100px;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
          transition: transform 0.2s;
        }

        .stat-pill:hover {
          transform: translateY(-2px);
          border-color: #cbd5e1;
        }

        .stat-value {
          font-weight: 800;
          color: var(--text-primary);
          font-size: 1.1rem;
        }

        .stat-label {
          color: var(--text-secondary);
          font-size: 0.9rem;
          font-weight: 500;
        }

        /* --- MANUAL SCROLL STYLES --- */
        .community-showcase {
          padding: 60px 0 100px;
          background: linear-gradient(180deg, var(--bg-main) 0%, #fff 100%);
        }

        .scroll-row-container {
          position: relative;
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          padding: 0 20px;
        }

        .scroll-content {
          display: flex;
          gap: 24px;
          overflow-x: auto;
          scroll-behavior: smooth;
          padding: 20px 4px; /* Space for shadows */
          width: 100%;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE/Edge */
        }
        
        .scroll-content::-webkit-scrollbar {
          display: none; /* Chrome/Safari */
        }

        /* Navigation Buttons */
        .nav-btn {
          position: absolute;
          z-index: 20;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 1px solid #e2e8f0;
          background: white;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transition: all 0.2s ease;
        }
        
        .nav-btn:hover {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
          transform: scale(1.1);
        }
        
        .nav-btn.left {
          left: 10px;
        }
        
        .nav-btn.right {
          right: 10px;
        }
        
        /* Edge Gradients */
        .edge-gradient {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 100px;
          pointer-events: none;
          z-index: 10;
        }
        
        .left-gradient {
          left: 0;
          background: linear-gradient(to right, var(--bg-main), transparent);
        }
        
        .right-gradient {
          right: 0;
          background: linear-gradient(to left, var(--bg-main), transparent);
        }

        /* --- CARDS --- */
        .student-card-wrapper {
          width: 320px;
          flex-shrink: 0;
          position: relative;
        }

        .modern-card {
          background: var(--card-bg);
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.03);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .student-card-wrapper:hover .modern-card {
          transform: translateY(-8px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.08);
          border-color: #cbd5e1;
        }

        .card-header-bg {
          height: 80px;
          background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
          border-bottom: 1px solid #eef2ff;
        }

        .card-body {
          padding: 0 24px 24px;
          margin-top: -40px;
          text-align: center;
        }

        .avatar-wrapper {
          position: relative;
          display: inline-block;
          margin-bottom: 16px;
        }

        .avatar-img {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid white;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
          background: white;
        }

        .status-indicator {
          position: absolute;
          bottom: 5px;
          right: 5px;
          width: 16px;
          height: 16px;
          background: #22c55e;
          border: 2px solid white;
          border-radius: 50%;
        }

        .student-name {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }

        .student-username {
          font-size: 0.9rem;
          color: var(--primary-color);
          font-weight: 600;
          margin-bottom: 12px;
        }

        .student-blurb {
          font-size: 0.925rem;
          color: var(--text-secondary);
          line-height: 1.5;
          margin-bottom: 24px;
          min-height: 42px; /* aligns cards */
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* --- BUTTONS --- */
        .action-stack {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .btn-link {
          text-decoration: none;
          display: block;
        }

        .modern-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 10px 16px;
          border-radius: 10px;
          border: none;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: var(--font-family);
        }

        .modern-btn.github {
          background: var(--github-color);
          color: white;
        }
        .modern-btn.github:hover {
          background: #1a1f24;
          transform: scale(1.02);
        }

        .modern-btn.linkedin {
          background: #e0f2fe; /* Light blue tint */
          color: var(--linkedin-color);
        }
        .modern-btn.linkedin:hover {
          background: #bae6fd;
          transform: scale(1.02);
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        /* --- FOOTER --- */
        .premium-footer {
          text-align: center;
          padding: 40px 20px;
          color: var(--text-secondary);
          font-size: 0.9rem;
          border-top: 1px solid #e2e8f0;
          background: white;
        }

        @media (max-width: 768px) {
          .nav-btn { width: 40px; height: 40px; }
          .scroll-row-container { padding: 0 10px; }
          .student-card-wrapper { width: 280px; }
          .hero-title { font-size: 2rem; }
        }
      `}</style>

      {/* Hero Section */}
      <section className="premium-hero">
        <div className="hero-bg-grid" />
        <div className="hero-content">
          <div className="icon-box">
            <GraduationCap size={40} color="white" />
          </div>
          
          <h1 className="hero-title">
            Logicmojo <span className="highlight-text">Data Science</span><br />
            Community
          </h1>
          
          <p className="hero-desc">
            Meet our vibrant community of data scientists, machine learning engineers, and AI enthusiasts building the future.
          </p>

          <div className="stats-row">
            <div className="stat-pill">
              <Users size={20} className="text-indigo-600" />
              <div>
                <span className="stat-value">{students.length}+</span> <span className="stat-label">Members</span>
              </div>
            </div>
            <div className="stat-pill">
              <TrendingUp size={20} className="text-pink-500" />
              <div>
                <span className="stat-value">500+</span> <span className="stat-label">Projects</span>
              </div>
            </div>
            <div className="stat-pill">
              <Sparkles size={20} className="text-amber-500" />
              <div>
                <span className="stat-value">98%</span> <span className="stat-label">Success</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manual Scroll Section */}
      <section className="community-showcase">
        <div style={{ marginBottom: '60px' }}>
          <ScrollRow students={firstRow} id="row1" />
        </div>
        
        <div>
          <ScrollRow students={secondRow} id="row2" />
        </div>
      </section>

      {/* Footer */}
      <footer className="premium-footer">
        <p>© 2025 Logicmojo Data Science Community. Empowering the next generation of data scientists.</p>
      </footer>
    </div>
  );
};

export default CommunitySection;